import React, { useEffect, useState } from 'react';
import {
  MapPin, Search, Trash2, Building, Home, X, Check,
  Grid3x3, List, Table as TableIcon, Map as MapIcon
} from 'lucide-react';
import api from '../../services/api';
import { AddButton } from '@/components/ui/AddButton';
import { useAlert } from '../../components/AlertProvider';

interface SalesPoint {
  id: number;
  name: string;
}

interface ClientAddress {
  id: number;
  client_id: number;
  sales_point_id: number | null;
  label: string | null;
  kind: 'billing' | 'shipping';
  address: string;
  city: string;
  postal_code: string;
  country: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const BuyerAddressesTailwind: React.FC = () => {
  const { showSuccess, showError } = useAlert();
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [filterKind, setFilterKind] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>('table');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<ClientAddress | null>(null);
  const [showMapPreview, setShowMapPreview] = useState<boolean>(false);
  const [listMapPreviewAddressId, setListMapPreviewAddressId] = useState<number | null>(null);

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed` : '';
  };
  const [form, setForm] = useState({
    sales_point_id: null as number | null,
    label: '',
    kind: 'billing' as 'billing' | 'shipping',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    is_default: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    void loadAll();
  }, []);

  const loadAll = async (): Promise<void> => {
    setBusy(true);
    try {
      const [addrRes, spRes] = await Promise.all([
        api.get('/clients/me/addresses'),
        api.get('/clients/me/sales-points'),
      ]);
      setAddresses(Array.isArray(addrRes.data) ? addrRes.data : []);
      setSalesPoints(Array.isArray(spRes.data) ? spRes.data : []);
    } catch (e) {
      console.error('Load error:', e);
      showError('Impossible de charger vos adresses');
    } finally {
      setBusy(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!form.address.trim()) {
      newErrors.address = 'L\'adresse est obligatoire';
    }
    if (!form.city.trim()) {
      newErrors.city = 'La ville est obligatoire';
    }
    if (!form.postal_code.trim()) {
      newErrors.postal_code = 'Le code postal est obligatoire';
    }
    if (!form.country.trim()) {
      newErrors.country = 'Le pays est obligatoire';
    }

    // Additional validation rules
    if (form.postal_code && !/^\d{4,5}$/.test(form.postal_code.trim())) {
      newErrors.postal_code = 'Le code postal doit contenir 4 ou 5 chiffres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openModal = (item?: ClientAddress): void => {
    if (item) {
      setEditing(item);
      setForm({
        sales_point_id: item.sales_point_id,
        label: item.label || '',
        kind: item.kind,
        address: item.address,
        city: item.city,
        postal_code: item.postal_code,
        country: item.country,
        is_default: item.is_default,
      });
    } else {
      setEditing(null);
      setForm({
        sales_point_id: null,
        label: '',
        kind: 'billing',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        is_default: false,
      });
    }
    setErrors({});
    setShowMapPreview(false);
    setShowModal(true);
  };

  const save = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = { ...form };
      if (editing) {
        await api.patch(`/clients/me/addresses/${editing.id}`, payload);
        showSuccess('Adresse mise à jour avec succès');
      } else {
        await api.post('/clients/me/addresses', payload);
        showSuccess('Adresse créée avec succès');
      }
      setShowModal(false);
      await loadAll();
    } catch (e: any) {
      console.error('Save error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const del = async (item: ClientAddress): Promise<void> => {
    if (!confirm(`Supprimer cette adresse ?`)) return;
    try {
      await api.delete(`/clients/me/addresses/${item.id}`);
      showSuccess('Adresse supprimée avec succès');
      await loadAll();
    } catch (e: any) {
      console.error('Delete error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const filtered = addresses.filter(item => {
    if (filterKind !== 'all' && item.kind !== filterKind) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.address.toLowerCase().includes(s) ||
      item.city.toLowerCase().includes(s) ||
      item.label?.toLowerCase().includes(s)
    );
  });

  const getSalesPointName = (spId: number | null): string => {
    if (!spId) return 'Global';
    const sp = salesPoints.find(s => s.id === spId);
    return sp ? sp.name : `PDV #${spId}`;
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Adresses</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {addresses.length} adresse{addresses.length > 1 ? 's' : ''} enregistrée{addresses.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <AddButton
          onClick={() => openModal()}
          label="Nouveau"
          className="w-full sm:w-auto"
        />
      </div>

      {/* Filters */}
      <div className="px-6 py-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une adresse..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => setFilterKind('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filterKind === 'all'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600'
                }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilterKind('billing')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filterKind === 'billing'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600'
                }`}
            >
              Facturation
            </button>
            <button
              onClick={() => setFilterKind('shipping')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filterKind === 'shipping'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600'
                }`}
            >
              Livraison
            </button>
          </div>

          <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 bg-white dark:bg-slate-800 ml-auto sm:ml-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid'
                ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              title="Vue Grille"
            >
              <Grid3x3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              title="Vue Liste"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'table'
                ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              title="Vue Tableau"
            >
              <TableIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 px-6 pb-6 min-h-0 ${viewMode === 'table' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <MapPin className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucune adresse</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => openModal(item)}
                className="p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:scale-105 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4 cursor-pointer"

                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.kind === 'billing'
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30'
                        : 'bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30'
                      }`}>

                      {item.kind === 'billing' ? (
                        <Building className={`h-5 w-5 ${item.kind === 'billing' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`} />
                      ) : (
                        <Home className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-900 dark:text-slate-100">
                          {item.label || (item.kind === 'billing' ? 'Facturation' : 'Livraison')}
                        </h3>
                        {item.is_default && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300">
                            <Check className="h-3 w-3 mr-1" />
                            Défaut
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {getSalesPointName(item.sales_point_id)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        del(item);
                      }} 
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                      aria-label="Supprimer"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-500" />
                    <div>
                      <div>{buildAddressComplete(item.address, item.postal_code, item.city) || '—'}</div>
                      {item.country && <div>{item.country}</div>}

                      {buildAddressComplete(item.address, item.postal_code, item.city) && (
                        <div className="mt-2">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setListMapPreviewAddressId((curr) => (curr === item.id ? null : item.id));
                              }}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                (listMapPreviewAddressId === item.id
                                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                              }
                            >
                              <MapIcon className="h-3.5 w-3.5" />
                              Carte
                            </button>
                            <a
                              href={buildMapsLink(buildAddressComplete(item.address, item.postal_code, item.city))}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Ouvrir dans Google Maps
                            </a>
                          </div>

                          {listMapPreviewAddressId === item.id && (
                            <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                              <iframe
                                title="Google Maps"
                                src={buildMapsEmbedSrc(buildAddressComplete(item.address, item.postal_code, item.city))}
                                className="w-full h-64"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-3">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => openModal(item)}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:translate-x-1 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer"
                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.kind === 'billing'
                    ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30'
                    : 'bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30'
                  }`}>
                  {item.kind === 'billing' ? (
                    <Building className={`h-6 w-6 ${item.kind === 'billing' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`} />
                  ) : (
                    <Home className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate">
                      {item.label || (item.kind === 'billing' ? 'Facturation' : 'Livraison')}
                    </h3>
                    {item.is_default && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300">
                        <Check className="h-3 w-3 mr-1" />
                        Défaut
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                      {getSalesPointName(item.sales_point_id)}
                    </span>
                    <span>•</span>
                    <span className="capitalize">{item.kind === 'billing' ? 'Facturation' : 'Livraison'}</span>
                  </div>

                  {buildAddressComplete(item.address, item.postal_code, item.city) && (
                    <div className="mt-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setListMapPreviewAddressId((curr) => (curr === item.id ? null : item.id));
                          }}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                            (listMapPreviewAddressId === item.id
                              ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                          }
                        >
                          <MapIcon className="h-3.5 w-3.5" />
                          Carte
                        </button>
                        <a
                          href={buildMapsLink(buildAddressComplete(item.address, item.postal_code, item.city))}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Ouvrir dans Google Maps
                        </a>
                      </div>

                      {listMapPreviewAddressId === item.id && (
                        <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <iframe
                            title="Google Maps"
                            src={buildMapsEmbedSrc(buildAddressComplete(item.address, item.postal_code, item.city))}
                            className="w-full h-64"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden sm:block text-sm text-right text-slate-600 dark:text-slate-400">
                  <div>{buildAddressComplete(item.address, item.postal_code, item.city) || '—'}</div>
                  {item.country && <div className="text-xs text-slate-500 dark:text-slate-500">{item.country}</div>}
                </div>

                <div className="flex gap-2 pl-4 border-l border-slate-200 dark:border-slate-700">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      del(item);
                    }} 
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg h-full flex flex-col min-h-0">
            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-950/30 border-b-2 border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Libellé / PDV</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Adresse</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur hidden sm:table-cell">Ville / CP</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Défaut</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filtered.map((item, idx) => (
                    <tr
                      key={item.id}
                      onClick={() => openModal(item)}
                      className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                      style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                    >
                      <td className="px-4 py-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.kind === 'billing'
                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                          {item.kind === 'billing' ? <Building className="h-4 w-4" /> : <Home className="h-4 w-4" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {item.label || (item.kind === 'billing' ? 'Facturation' : 'Livraison')}
                        </div>
                        <div className="text-xs text-slate-500">
                          {getSalesPointName(item.sales_point_id)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs">
                        <div className="truncate">{buildAddressComplete(item.address, item.postal_code, item.city) || '—'}</div>
                        {item.country && <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{item.country}</div>}

                        {buildAddressComplete(item.address, item.postal_code, item.city) && (
                          <div className="mt-2">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setListMapPreviewAddressId((curr) => (curr === item.id ? null : item.id));
                                }}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                  (listMapPreviewAddressId === item.id
                                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                }
                              >
                                <MapIcon className="h-3.5 w-3.5" />
                                Carte
                              </button>
                              <a
                                href={buildMapsLink(buildAddressComplete(item.address, item.postal_code, item.city))}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Ouvrir dans Google Maps
                              </a>
                            </div>

                            {listMapPreviewAddressId === item.id && (
                              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                <iframe
                                  title="Google Maps"
                                  src={buildMapsEmbedSrc(buildAddressComplete(item.address, item.postal_code, item.city))}
                                  className="w-full h-64"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 hidden sm:table-cell">
                        {item.postal_code} {item.city}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.is_default && (
                          <div className="inline-flex justify-center">
                            <Check className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            del(item);
                          }} 
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors inline-block"
                          aria-label="Supprimer"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{editing ? 'Modifier' : 'Nouvelle'} Adresse</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Facturation ou livraison</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowMapPreview(false);
                  setShowModal(false);
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                    Type d'adresse <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="kind"
                        value="billing"
                        checked={form.kind === 'billing'}
                        onChange={(e) => setForm({ ...form, kind: e.target.value as 'billing' | 'shipping' })}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>Facturation</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="kind"
                        value="shipping"
                        checked={form.kind === 'shipping'}
                        onChange={(e) => setForm({ ...form, kind: e.target.value as 'billing' | 'shipping' })}
                        className="w-4 h-4 text-cyan-600"
                      />
                      <span>Livraison</span>
                    </label>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="client_address_sales_point" className="block text-sm font-medium mb-1">Point de vente (optionnel)</label>
                  <select
                    id="client_address_sales_point"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.sales_point_id || ''}
                    onChange={(e) => setForm({ ...form, sales_point_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  >
                    <option value="">Global (tous les points de vente)</option>
                    {salesPoints.map(sp => (
                      <option key={sp.id} value={sp.id}>{sp.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="client_address_label" className="block text-sm font-medium mb-1">Libellé (optionnel)</label>
                  <input
                    id="client_address_label"
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="Ex: Siège social, Entrepôt..."
                  />
                </div>

                <div className="md:col-span-2 border-2 border-cyan-300 p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/20">
                  <label className="block text-sm font-medium mb-3 text-cyan-700 dark:text-cyan-300">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Adresse
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="client_address_street" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                      <input
                        id="client_address_street"
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 transition-colors ${errors.address
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-slate-300 dark:border-slate-600 focus:border-cyan-500 focus:ring-cyan-500'
                        } focus:outline-none focus:ring-2`}
                        value={form.address}
                        onChange={(e) => {
                          setForm({ ...form, address: e.target.value });
                          if (errors.address) setErrors({ ...errors, address: '' });
                        }}
                        placeholder="Ex: 44 Avenue Gambetta"
                        required
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.address}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="client_address_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                      <input
                        id="client_address_postal_code"
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 transition-colors ${errors.postal_code
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-slate-300 dark:border-slate-600 focus:border-cyan-500 focus:ring-cyan-500'
                        } focus:outline-none focus:ring-2`}
                        value={form.postal_code}
                        onChange={(e) => {
                          setForm({ ...form, postal_code: e.target.value });
                          if (errors.postal_code) setErrors({ ...errors, postal_code: '' });
                        }}
                        placeholder="Ex: 17100"
                        required
                      />
                      {errors.postal_code && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.postal_code}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="client_address_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                      <input
                        id="client_address_city"
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 transition-colors ${errors.city
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-slate-300 dark:border-slate-600 focus:border-cyan-500 focus:ring-cyan-500'
                        } focus:outline-none focus:ring-2`}
                        value={form.city}
                        onChange={(e) => {
                          setForm({ ...form, city: e.target.value });
                          if (errors.city) setErrors({ ...errors, city: '' });
                        }}
                        placeholder="Ex: Saintes"
                        required
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.city}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="client_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                      <input
                        id="client_address_complete"
                        className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600"
                        value={buildAddressComplete(form.address, form.postal_code, form.city)}
                        readOnly
                      />

                      {buildAddressComplete(form.address, form.postal_code, form.city) && (
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setShowMapPreview((v) => !v)}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                              (showMapPreview
                                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                            }
                          >
                            <MapIcon className="h-3.5 w-3.5" />
                            Carte
                          </button>
                          <a
                            href={buildMapsLink(buildAddressComplete(form.address, form.postal_code, form.city))}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            Ouvrir dans Google Maps
                          </a>
                        </div>
                      )}

                      {showMapPreview && (
                        <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <iframe
                            title="Google Maps"
                            src={buildMapsEmbedSrc(buildAddressComplete(form.address, form.postal_code, form.city))}
                            className="w-full h-64"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                    Pays <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 transition-colors ${errors.country
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-slate-600 focus:border-cyan-500 focus:ring-cyan-500'
                      } focus:outline-none focus:ring-2`}
                    value={form.country}

                    onChange={(e) => {
                      setForm({ ...form, country: e.target.value });
                      if (errors.country) {
                        setErrors({ ...errors, country: '' });
                      }
                    }}
                    placeholder="Ex: France"
                    required
                  />
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.country}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_default}
                      onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 rounded"

                    />
                    <span className="text-sm font-medium">Adresse par défaut pour ce type</span>
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">
                    Sera utilisée automatiquement lors de la création de commandes
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Annuler</button>
                <button onClick={save} className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors font-medium">Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerAddressesTailwind;
